/**
 * Utility functions for StudioAI feature
 */

import { ROLE_TYPES, STREAMING_EVENT_TYPES, TEMP_MESSAGE_IDS } from '@/@studio-ai/constants';
import type { ChatMessage, ChatMessageContent, ExtractionMethod } from '../types';

const PENDING_AI_ID_PREFIX = 'pending-ai_';

/**
 * Mint a unique pending-id for the AI message a streaming mutation is
 * about to insert. The id is scoped by conversationId and salted with a
 * uuid so two concurrent (or one cancelled, one new) streams can never
 * collide and so `updateMessageById` can drop its old "fall back to the
 * shared pending-ai-id constant" branch.
 */
export function createPendingAiId(conversationId: string | null): string {
    const scope = conversationId ?? 'new';
    const cryptoObj = (globalThis as { crypto?: Crypto }).crypto;
    const uuid =
        cryptoObj && typeof cryptoObj.randomUUID === 'function'
            ? cryptoObj.randomUUID()
            : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    return `${PENDING_AI_ID_PREFIX}${scope}_${uuid}`;
}

/**
 * True for any id minted by `createPendingAiId`, regardless of which
 * conversation it was minted for.
 */
export function isPendingAiId(id: string | null | undefined): boolean {
    return typeof id === 'string' && id.startsWith(PENDING_AI_ID_PREFIX);
}

// Re-export utilities from other files for convenience
export * from './history-utils';
export * from './streaming-utils';
export * from './text-utils';

// ============================================================================
// ARRAY UTILITIES
// ============================================================================

/**
 * Helper function to find last index (compatible with older TypeScript versions)
 */
export function findLastIndex<T>(array: T[], predicate: (item: T) => boolean): number {
    for (let i = array.length - 1; i >= 0; i--) {
        if (predicate(array[i])) {
            return i;
        }
    }
    return -1;
}

// ============================================================================
// MESSAGE UTILITIES
// ============================================================================

/**
 * Generate a unique conversation ID
 */
export function generateConversationId(): string {
    return `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Document attachment info for user messages (extracted document)
 */
export interface DocumentAttachmentInfo {
    fileName: string;
    contentType: string;
    size: number;
    extractedText: string;
    extractionMethod: ExtractionMethod;
}

/**
 * Create a user message object
 * If a document is attached, the content will be an array with document_attachment and text content
 */
export function createUserMessage(
    prompt: string,
    messageId?: string,
    documentAttachment?: DocumentAttachmentInfo,
): ChatMessage {
    // If there's a document attachment, create array content
    if (documentAttachment) {
        return {
            id: messageId || TEMP_MESSAGE_IDS.PENDING_USER,
            role: ROLE_TYPES.USER,
            content: [
                {
                    type: 'document_attachment' as const,
                    document_attachment: documentAttachment,
                },
                {
                    type: STREAMING_EVENT_TYPES.TEXT,
                    text: prompt,
                },
            ],
            timestamp: new Date(),
        };
    }

    // No document, just text content
    return {
        id: messageId || TEMP_MESSAGE_IDS.PENDING_USER,
        role: ROLE_TYPES.USER,
        content: prompt,
        timestamp: new Date(),
    };
}

/**
 * Create an AI message object
 */
export function createAIMessage(id: string): ChatMessage {
    return {
        id,
        role: ROLE_TYPES.ASSISTANT,
        content: [] as ChatMessageContent[],
        timestamp: new Date(),
        metadata: {},
        isStreaming: true,
    };
}

// ============================================================================
// CONTENT UTILITIES
// ============================================================================

/**
 * Convert message content to Snowflake-compatible format
 */
export function convertToSnowflakeFormat(messages: ChatMessage[]): Array<Record<string, unknown>> {
    return messages
        .map((msg) => {
            const snowflakeMessage: Record<string, unknown> = {
                role: msg.role,
                content: [],
            };

            if (typeof msg.content === 'string') {
                // Handle legacy string content
                snowflakeMessage.content = [
                    { type: STREAMING_EVENT_TYPES.TEXT, text: msg.content },
                ];
            } else if (Array.isArray(msg.content)) {
                // Convert our content array to Snowflake format
                const snowflakeContent: Array<Record<string, unknown>> = [];

                msg.content.forEach((contentItem) => {
                    switch (contentItem.type) {
                        case STREAMING_EVENT_TYPES.TEXT:
                            if (contentItem.text && contentItem.text.trim()) {
                                snowflakeContent.push({
                                    type: STREAMING_EVENT_TYPES.TEXT,
                                    text: contentItem.text,
                                });
                            }
                            break;
                    }
                });

                snowflakeMessage.content = snowflakeContent;
            }

            return snowflakeMessage;
        })
        .filter((msg) => {
            // Only include messages that have content
            const content = msg.content as Array<Record<string, unknown>>;
            return content && content.length > 0;
        });
}

/**
 * Find existing text content in message content array
 */
export function findExistingTextContent(content: ChatMessageContent[]): number {
    return content.findIndex((item) => item.type === STREAMING_EVENT_TYPES.TEXT);
}

/**
 * Find existing thinking content in message content array
 */
export function findExistingThinkingContent(content: ChatMessageContent[]): number {
    return content.findIndex((item) => item.type === STREAMING_EVENT_TYPES.THINKING);
}

/**
 * Append text to existing content or create new text content
 */
export function appendTextContent(
    content: ChatMessageContent[],
    newText: string,
): ChatMessageContent[] {
    const existingTextIndex = findExistingTextContent(content);

    if (existingTextIndex !== -1) {
        // Append to existing text
        const existingText = content[existingTextIndex] as { type: 'text'; text: string };
        const updatedContent = [...content];
        updatedContent[existingTextIndex] = {
            ...existingText,
            text: existingText.text + newText,
        };
        return updatedContent;
    } else {
        // Add new text content
        return [
            ...content,
            {
                type: STREAMING_EVENT_TYPES.TEXT,
                text: newText,
            },
        ];
    }
}

/**
 * Append thinking content to message
 */
export function appendThinkingContent(
    content: ChatMessageContent[],
    newText: string,
): ChatMessageContent[] {
    const existingThinkingIndex = findExistingThinkingContent(content);

    if (existingThinkingIndex !== -1) {
        // Append to existing thinking content
        const existingThinking = content[existingThinkingIndex] as {
            type: 'thinking';
            text: string;
        };

        const updatedContent = [...content];
        updatedContent[existingThinkingIndex] = {
            ...existingThinking,
            text: existingThinking.text + newText,
        };
        return updatedContent;
    } else {
        // Create new thinking content
        return [
            ...content,
            {
                type: STREAMING_EVENT_TYPES.THINKING,
                text: newText,
            },
        ];
    }
}

/**
 * Ensure content is an array format
 */
export function ensureContentArray(content: string | ChatMessageContent[]): ChatMessageContent[] {
    if (typeof content === 'string') {
        return [
            {
                type: STREAMING_EVENT_TYPES.TEXT,
                text: content,
            },
        ];
    }
    return content;
}

// ============================================================================
// MESSAGE PROCESSING UTILITIES
// ============================================================================

/**
 * Update an assistant message in `messages` by id. The id must match
 * exactly: there is no shared-constant fallback. Pending ids are minted
 * per-mutation by `createPendingAiId(conversationId)` so a missing id
 * reliably means "this message is no longer in our state" (e.g. the
 * user navigated away mid-stream) and we should leave the array alone.
 */
export function updateMessageById(
    messages: ChatMessage[],
    messageId: string,
    updates: Partial<ChatMessage>,
): ChatMessage[] {
    const messageIndex = findLastIndex(
        messages,
        (message) => message.role === ROLE_TYPES.ASSISTANT && message.id === messageId,
    );

    if (messageIndex !== -1) {
        const newMessages = [...messages];
        newMessages[messageIndex] = {
            ...newMessages[messageIndex],
            ...updates,
            timestamp: new Date(),
        };
        return newMessages;
    }

    return messages;
}

/**
 * Create a deep copy of message content
 */
export function cloneMessageContent(content: ChatMessageContent[]): ChatMessageContent[] {
    return content.map((item) => ({ ...item }));
}

/**
 * Extract plain text from a ChatMessage's content for clipboard copying.
 * Concatenates all text-type content blocks, ignoring thinking/tool/chart artifacts.
 */
export function extractMessageText(content: string | ChatMessageContent[]): string {
    if (typeof content === 'string') return content;

    return content
        .filter((item) => item.type === STREAMING_EVENT_TYPES.TEXT && item.text)
        .map((item) => item.text!.trim())
        .join('\n\n');
}

/**
 * DANI is still answering: the last message is the person's, or DANI's reply is
 * still streaming. What that reply holds is not known yet, so nothing should
 * offer a next step the reply may take itself (UAT F55).
 */
export function isAwaitingReply(
    messages: readonly { role?: string; isStreaming?: boolean }[],
): boolean {
    const last = messages[messages.length - 1];
    return !!last && (last.role !== 'assistant' || !!last.isStreaming);
}
